import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { InterwillDto } from '@/entities/interwills';
import { useScenariosActions, useScenariosList } from '@/entities/scenarios';
import { useNotices } from '@/features/notices/useNotices';
import { routes } from '@/shared/helpers';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { getDtoRelatedToTargetDto } from '@/shared/lib/helpers/getDtoRelatedToTargetDto';
import {
  AssetDto,
  BeneficiaryDto,
  CollectionName,
  CollectionToDtoMap,
  NAME_KEY_BY_ASSET_CATEGORY,
  PersonalIdentifierDocumentDto,
  PrivateDocumentDto,
  useSJDatabase,
} from '@/shared/lib/sj-orm';
import { useModalsStore } from '@/widgets/modals';

import { useBeneficiary } from './useBeneficiary';

export type BeneficiaryRelationItem = {
  id: string;
  title: string;
  link: string;
  itemType: CollectionName;
};

const allowedCollections = [
  CollectionName.ASSETS,
  CollectionName.INTERWILLS,
  CollectionName.PRIVATE_DOCUMENTS,
  CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
];

export const useBeneficiaryUiActions = () => {
  const { t } = useTranslation('entities/beneficiaries');
  const notices = useNotices();
  const { deleteBeneficiary } = useBeneficiary();
  const { scenarios } = useScenariosList();
  const { findByDatabaseId: findScenarioMetaInfoByDatabaseId } = useScenariosActions();
  const router = useAppRouter();
  const sjDb = useSJDatabase();
  const modals = useModalsStore();

  return useMemo(() => {
    const checkAndDeleteBeneficiary = (beneficiary: BeneficiaryDto) => {
      const relatedDtos: Map<
        keyof CollectionToDtoMap,
        Set<CollectionToDtoMap[keyof CollectionToDtoMap]>
      > = getDtoRelatedToTargetDto(sjDb, beneficiary, false);

      const relatedScenarios =
        scenarios?.filter((scenario) =>
          scenario.transfers.some(
            (transfer) => transfer.recipientProfileId === beneficiary.centralDbProfileId,
          ),
        ) ?? [];

      const relations: BeneficiaryRelationItem[] = [];

      relatedDtos.forEach((dtoSet, collectionName) => {
        if (!allowedCollections.includes(collectionName)) return;

        dtoSet.forEach((dto) => {
          switch (collectionName) {
            case CollectionName.ASSETS: {
              const asset = dto as AssetDto;
              const nameKey = NAME_KEY_BY_ASSET_CATEGORY[asset.category];

              relations.push({
                id: asset.id,
                title: String(asset[nameKey]),
                link: routes.asset(asset.id),
                itemType: collectionName,
              });
              break;
            }

            case CollectionName.INTERWILLS: {
              const interwill = dto as InterwillDto;
              relations.push({
                id: interwill.id,
                title: 'Signed Will information of Testator',
                link: '/interwill',
                itemType: collectionName,
              });
              break;
            }

            case CollectionName.PRIVATE_DOCUMENTS:
            case CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS: {
              const doc = dto as PrivateDocumentDto | PersonalIdentifierDocumentDto;
              relations.push({
                id: doc.id,
                title: doc.name ?? 'Document',
                link: `/documents/${doc.id}`,
                itemType: collectionName,
              });
              break;
            }

            default:
              break;
          }
        });
      });

      relatedScenarios.forEach((scenario) => {
        const scenarioMetaInfo = findScenarioMetaInfoByDatabaseId(scenario.id);

        relations.push({
          id: scenario.id,
          title: scenarioMetaInfo?.name ?? 'Transfer algorithm',
          link: `/scenarios/${scenario.id}`,
          itemType: CollectionName.SCENARIO_META_INFO,
        });
      });

      if (relations.length > 0) {
        modals.openModal({ type: 'BeneficiaryRelationsModal', relations });
        return;
      }

      notices.antd.confirm(t('removeConfirm'), () => {
        deleteBeneficiary(beneficiary);
        router.pushShallow(routes.beneficiaries());
      });
    };

    return {
      checkAndDeleteBeneficiary,
    };
  }, []);
};
